import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
} from '@material-ui/core';
import i18n from 'i18n-js';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import PrintIcon from '@material-ui/icons/Print';

import { pagingOptions } from 'constants/arrays';
import { useStyles } from '../styles';
import globalStore from 'store/globalStore';
import agent from 'api/agent';
import { useAppSelector } from 'index';
import { formatDateIntl } from 'helpers/datetimeHelper';
import { ClosingReport } from '..';

export default function PeriodClosingsTab() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const language = useAppSelector((state) => state.global.language);
    const [count, setCount] = useState(0);
    const [reports, setReports] = useState<ClosingReport[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pagingOptions[0]);

    async function downloadReportPDF(id: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            await agent.Payments.GenerateClosinfReportPDF(id);
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    async function printReportPDF(id: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            await agent.Payments.GenerateClosinfReportPDFForPrint(id);
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    useEffect(() => {
        const getReports = async () => {
            dispatch(globalStore.actionCreators.showSpiner());
            try {
                const closings = await agent.Payments.GetClosingsReports(page, rowsPerPage);
                if (closings) {
                    setReports(closings.list);
                    setCount(closings.count);
                }
            } catch (error) {
                dispatch(globalStore.actionCreators.showToaster('error', error));
            } finally {
                dispatch(globalStore.actionCreators.hideSpiner());
            }
        };
        getReports();
    }, [page, rowsPerPage]);

    return (
        <Paper>
            <TableContainer id="container" style={{ maxHeight: '90%' }}>
                <Table stickyHeader className={classes.root} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                className={classes.tableCellHeader}
                                style={{ paddingLeft: 18, width: 100 }}
                            >
                                <Box className={classes.tableCellHeaderContainer}>
                                    {i18n.t('common.type')}
                                </Box>
                            </TableCell>
                            <TableCell className={classes.tableCellHeader} width={'22%'}>
                                <Box className={classes.tableCellHeaderContainer}>
                                    {i18n.t('common.start')}
                                </Box>
                            </TableCell>
                            <TableCell className={classes.tableCellHeader} width={'22%'}>
                                <Box className={classes.tableCellHeaderContainer}>
                                    {i18n.t('common.end')}
                                </Box>
                            </TableCell>
                            <TableCell className={classes.tableCellHeader} width={'18%'}>
                                <Box className={classes.tableCellHeaderContainer}>
                                    {i18n.t('common.total')}
                                </Box>
                            </TableCell>
                            <TableCell className={classes.tableCellHeader} width={'18%'}>
                                <Box className={classes.tableCellHeaderContainer}>
                                    {i18n.t('paymentDetails.vat')}
                                </Box>
                            </TableCell>
                            <TableCell className={classes.tableCellHeader}>
                                <div
                                    style={{ padding: 16, width: 100 }}
                                    className={classes.tableCellHeaderContainer}
                                >
                                    {i18n.t('common.actions')}
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reports.map((report) => (
                            <TableRow key={report.id}>
                                <TableCell align={'left'}>
                                    <Box component="span"> {report.type}</Box>
                                </TableCell>
                                <TableCell>
                                    <Box component="span">
                                        {formatDateIntl(report.dateFrom, language)}
                                    </Box>
                                </TableCell>
                                <TableCell align={'left'}>
                                    <Box component="span">
                                        {formatDateIntl(report.dateTo, language)}
                                    </Box>
                                </TableCell>
                                <TableCell align={'left'}>
                                    <Box component="span">{report.totalAmount}</Box>
                                </TableCell>
                                <TableCell align={'left'}>
                                    <Box component="span">{report.totalTax}</Box>
                                </TableCell>
                                <TableCell align={'left'}>
                                    <IconButton
                                        aria-label="delete"
                                        size="small"
                                        className={classes.iconButton}
                                        style={{ marginLeft: 10 }}
                                        onClick={() => downloadReportPDF(report.id)}
                                    >
                                        <PictureAsPdfIcon fontSize="medium" color="primary" />
                                    </IconButton>
                                    <IconButton
                                        aria-label="delete"
                                        size="small"
                                        className={classes.iconButton}
                                        style={{ marginLeft: 10 }}
                                        onClick={() => printReportPDF(report.id)}
                                    >
                                        <PrintIcon fontSize="medium" color="primary" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={pagingOptions}
                component="div"
                count={count}
                rowsPerPage={rowsPerPage}
                labelRowsPerPage={i18n.t('common.rowsPerPage')}
                page={page}
                onPageChange={(_, page) => setPage(page)}
                onRowsPerPageChange={(event) => setRowsPerPage(+event.target.value)}
            />
        </Paper>
    );
}
